import React from 'react';
import type { LabeledVizSpec } from 'shared/src/api/ai-labeled-viz-types';
import type { NoDataViz } from 'shared/src/api/ai-viz-types';
import type { CubeMemberInfo } from 'shared/src/trpc-types/cube-load-trpc-types';
import { memberTitle } from './cube-member-utils';
import { VizTypeBadge } from './viz-type-badge';

type VizRole = { label: string; value: string };

export type VizSummaryProps = {
  labeledViz: LabeledVizSpec | NoDataViz;
  memberMeta?: Record<string, CubeMemberInfo>;
};

export function VizSummary({ labeledViz, memberMeta }: VizSummaryProps) {
  const roles = extractVizRoles(labeledViz, memberMeta);

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <VizTypeBadge vizType={labeledViz.vizType} />
      </div>
      <div className="space-y-1.5">
        {roles.map(r => (
          <div key={r.label} className="flex gap-1.5 text-xs">
            <span className="shrink-0 text-muted-foreground">{r.label}:</span>
            <span className="font-medium text-foreground">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function extractVizRoles(
  viz: LabeledVizSpec | NoDataViz,
  meta?: Record<string, CubeMemberInfo>
): VizRole[] {
  const t = (ref: string) => memberTitle(ref, meta);

  switch (viz.vizType) {
    case 'line_bar_graph':
      return [
        { label: 'Time', value: t(viz.mapping.timeKey) },
        { label: 'Planned', value: t(viz.mapping.plannedKey) },
        { label: 'Values', value: viz.mapping.barKeys.map(t).join(', ') }
      ];
    case 'bar_chart':
      return [
        { label: 'Category', value: t(viz.mapping.categoryKey) },
        { label: 'Values', value: viz.mapping.valueKeys.map(t).join(', ') }
      ];
    case 'line_chart':
      return [
        { label: 'Time', value: t(viz.mapping.timeKey) },
        { label: 'Lines', value: viz.mapping.lineKeys.map(t).join(', ') }
      ];
    case 'kpi_card': {
      const roles: VizRole[] = [{ label: 'Value', value: t(viz.mapping.valueKey) }];
      if (viz.mapping.deltaKey) roles.push({ label: 'Delta', value: t(viz.mapping.deltaKey) });
      return roles;
    }
    case 'pie_donut':
      return [
        { label: 'Category', value: t(viz.mapping.categoryKey) },
        { label: 'Value', value: t(viz.mapping.valueKey) }
      ];
    case 'table':
      return [{ label: 'Columns', value: viz.columns.map(c => t(c.key)).join(', ') }];
    case 'no_data':
      return [{ label: 'Reason', value: viz.reason }];
  }
}
